<template>
  <div class="team-header">
    <div class="team-header__background">
      <team-image :team-id="teamId" :alt="teamData.name" />
    </div>

    <!-- Team Header Content -->
    <div class="team-header__content">
      <!-- Title Row: Team Name on Left, Buttons on Right -->
      <div class="team-header__title-row">
        <h1 class="team-header__title" v-if="teamData.name">
          {{ teamData.name }}
        </h1>
        <div class="team-header__buttons" v-if="isUserLoggedIn">
          <el-button type="primary" v-if="!teamData.hasAdminRights" @click="isContactAdminFormVisible = true">
            Contact captain
          </el-button>
          <!-- For team admins, also show Invite teammates and Edit -->
          <el-button type="primary" v-if="teamData.hasAdminRights" @click="toSearchTeammates">
            Invite teammates
          </el-button>
          <el-button type="primary" v-if="teamData.hasAdminRights" @click="editTeam">
            Edit
          </el-button>
          <!-- Share button is always visible -->
          <el-button type="primary" @click="handleShare" disabled>
            Share
          </el-button>
        </div>
      </div>

      <!-- Team Motto -->
      <div class="team-header__motto" v-if="teamData.motto">
        {{ teamData.motto }}
      </div>

      <!-- Activity and Location Info -->
      <div class="team-header__info" v-if="teamData.activityId || teamData.location">
        <activity-icon class="team-header__activity-icon" :size="32" :activity="teamData.activityId" />
        <span class="team-header__activity-name">{{ teamData.activityName }}</span>
        <span class="team-header__separator">&middot;</span>
        <i class="el-icon-location-outline team-header__location-icon"></i>
        <a class="team-header__location"
           :href="`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(teamData.location)}`"
           target="_blank" rel="noopener noreferrer">
          {{ teamData.location }}
        </a>
        <span v-if="teamData.demo" class="team-header__separator">&middot;</span>
        <el-tag v-if="teamData.demo" type="primary" effect="dark">
          Demo
        </el-tag>
      </div>

      <!-- External Website Link -->
      <div class="team-header__website" v-if="teamData.webSite">
        <a :href="teamData.webSite" target="_blank" rel="noopener noreferrer">
          <el-icon class="team-header__icon">
            <HomeFilled />
          </el-icon>
          {{ teamData.webSite }}
        </a>
      </div>

      <!-- Social Networks Icons -->
      <div class="team-header__social-networks" v-if="teamData?.teamSocialNetworks?.length > 0">
        <a class="team-header__social"
           v-for="(social, index) in teamData.teamSocialNetworks"
           :key="index"
           :href="social.socialNetworkBaseUrl + social.accountLink"
           target="_blank">
          <el-avatar :size="32">
            <el-image :src="social.iconURL" :alt="social.name" :key="social.name" :size="32" />
          </el-avatar>
        </a>
      </div>
    </div>

    <!-- Contact Organizer Dialog (unchanged) -->
    <el-dialog v-model="isContactAdminFormVisible" width="40%" title="Contact organizer"
               v-if="!teamData.hasAdminRights">
      <el-form :rules="contactAdminRules" :model="contactAdminForm" ref="contactAdminForm">
        <el-form-item prop="text" :error="contactAdminErrors.text" label="Message">
          <el-input v-model="contactAdminForm.text" type="textarea" :rows="4"></el-input>
        </el-form-item>
      </el-form>

      <template #footer>
        <span class="dialog-footer">
          <el-button @click="isContactAdminFormVisible = false">
            Cancel
          </el-button>
          <el-button type="primary" @click="validateContactAdminForm" :disabled="isButtonDisabled('contact-admin')">
            Send message
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import Icon from '@/components/common/Icon.vue';
import TeamImage from '@/components/teams/TeamImage.vue';
import ActivityIcon from '../common/ActivityIcon.vue';
import { formatDate, SHORT_DATETIME_FORMAT, parseErrorsByFields } from '@/utils/index.js';
import { showMessage } from '@/utils/messages.js';
import { sendMessageToTeamCaptain } from '@/api/team.js';
import { disableButton } from '@/mixins/disableButton.js';
import { getSocialNetworkIconURL } from '@/api/images.js';

export default {
  name: 'TeamHeader',
  props: {
    teamData: {
      type: Object,
      required: true,
    },
    teamId: {
      type: Number,
      required: true,
    },
    isUserLoggedIn: {
      type: Boolean,
      required: false,
    },
  },
  mixins: [disableButton],
  components: {
    Icon,
    TeamImage,
    ActivityIcon,
  },
  data() {
    return {
      SHORT_DATETIME_FORMAT,
      isContactAdminFormVisible: false,
      contactAdminForm: { text: null },
      contactAdminRules: {
        text: [
          {
            required: true,
            trigger: 'input',
            message: 'Please enter your message',
          },
        ],
      },
      contactAdminErrors: { text: null },
    };
  },
  methods: {
    formatDate,
    showMessage,
    parseErrorsByFields,
    async requestContactAdmin() {
      this.disableButton('contact-admin');
      const { teamId } = this;
      const { text } = this.contactAdminForm;
      const { success, errors } = await sendMessageToTeamCaptain({
        teamId,
        text,
      });
      this.enableButton('contact-admin');
      if (success) {
        this.showMessage({
          type: 'success',
          message: 'Your message has been sent',
        });
        this.isContactAdminFormVisible = false;
        this.contactAdminForm.text = null;
        this.contactAdminErrors.text = null;
      } else if (errors) {
        this.contactAdminErrors = {
          ...this.contactAdminErrors,
          ...this.parseErrorsByFields(errors),
        };
      }
    },
    handleShare() {
      console.log('share');
    },
    editTeam() {
      const { teamId } = this;
      this.$router.push({
        name: 'TeamForm',
        query: {
          edit: true,
          teamId,
        },
      });
    },
    convertToLowerCase(data) {
      return data.toLowerCase();
    },
    validateContactAdminForm() {
      const { text } = this.contactAdminForm;
      if (this.$refs.contactAdminForm) {
        this.$refs.contactAdminForm.validate();
      }
      if (text && text.trim()) {
        this.requestContactAdmin();
      }
    },
    toSearchTeammates() {
      const { teamId } = this;
      this.$router.push({
        name: 'SearchTeammates',
        query: {
          teamId,
        },
      });
    },
  },
  async mounted() {
    for (let i = 0; i < this.teamData?.teamSocialNetworks.length; i++) {
      this.teamData.teamSocialNetworks[i].iconURL = await getSocialNetworkIconURL({
        name: this.teamData.teamSocialNetworks[i].name,
      });
    }
  },
};
</script>

<style lang="scss">
@import '~@/styles/shared/_globals.scss';

.team-header {
  overflow: hidden;
  padding: 0 var(--global-dimension-size-300) var(--global-dimension-size-400);
  background-color: var(--color-gray-50);
  border-radius: var(--border-radius-base);
  margin-bottom: 30px;
  line-height: 1.6;

  &__background {
    position: relative;
    min-width: 100%;
    height: 200px;
    margin-left: -24px; // --global-dimension-size-300
    margin-right: -24px;

    img {
      position: absolute;
      width: 100%;
      height: 100%;

      object-fit: cover;
      object-position: center;
    }
  }

  // Main content container
  &__content {
    padding: var(--global-dimension-size-100);
  }

  // Title row: team name and buttons aligned horizontally
  &__title-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: var(--global-dimension-size-100);

    .team-header__title {
      margin: 0;
      font-size: var(--font-size-800);
      font-weight: 500;
    }

    .team-header__buttons {
      display: flex;
      gap: 2px;  /* Decreased spacing between buttons */
      margin-top: 8px;
    }
  }

  // Motto styling (elegant italic)
  &__motto {
    font-size: var(--font-size-300);
    font-style: italic;
    color: var(--color-gray-700);
    margin-bottom: var(--global-dimension-size-100);
  }

  // Activity and location info
  &__info {
    display: flex;
    align-items: center;
    font-size: var(--font-size-200);
    color: var(--color-gray-900);
    margin-bottom: var(--global-dimension-size-100);

    .team-header__activity-icon {
      margin-right: var(--global-dimension-size-100);
    }

    .team-header__activity-name {
      margin-right: var(--global-dimension-size-100);
    }

    .team-header__separator {
      margin: 0 var(--global-dimension-size-50);
    }

    .team-header__location-icon {
      margin-right: var(--global-dimension-size-50);
    }

    .team-header__location {
      color: var(--color-primary-500);
      text-decoration: underline;
    }
  }

  // Website link styling to highlight it as a link
  &__website {
    margin-bottom: var(--global-dimension-size-100);

    a {
      color: var(--color-primary-500);
      text-decoration: underline;
      font-size: var(--font-size-200);
      display: inline-flex;
      align-items: center;

      .team-header__icon {
        margin-right: var(--global-dimension-size-50);
      }
    }
  }

  // Social networks icons in a row with tiny spacing
  &__social-networks {
    display: flex;
    gap: 4px;
  }

  // Responsive adjustments
  @media (max-width: 768px) {
    &__title-row {
      flex-direction: column;
      align-items: flex-start;

      .team-header__buttons {
        margin-top: var(--global-dimension-size-100);
      }
    }
  }
}
</style>
